// combatpro/treasure/TreasureView.js
//
// The full Treasure subsystem page. Three coherent parts over ONE engine + store:
//   1. Monster Search - every populated monster instance in the active encounter,
//      each opening the SAME per-monster treasure store the fast modal uses.
//   2. General Generator - Individual / Group / Hoard / Container / Reward / Custom,
//      with level / wealth / quantity / context, composition %, coin composition,
//      and a result workspace (generate / reroll all / reroll one / edit / add /
//      remove / clear / save).
//   3. History - server-backed saved general results (treasureLog), reload / delete.
//
// Monster treasure and general history persist through CombatContext into the
// existing server-backed encounter document. No optional chaining, no em dash.

import React, { useContext, useState } from 'react';
import { CombatContext } from '../contexts/CombatContext';
import { useTreasureCatalogue } from './useTreasureCatalogue';
import { deriveMonsterContext } from './treasureMonster';
import { deriveTabName } from '../monsters/monsterEngine';
import { generateTreasure } from './treasureEngine';
import { withUids, removeItem, upsertItem, rerollItem } from './treasureResultOps';
import { WEALTH_TIERS, MODES, CATEGORIES } from './treasureVocab';
import { categoryLabel, formatValue } from './treasureFormat';
import TreasureResult from './TreasureResult';
import MonsterTreasureModal from './MonsterTreasureModal';
import GeneratorProvenanceWorkspace from '../integration/GeneratorProvenanceWorkspace';

var QUANT = ['sparse', 'normal', 'generous', 'huge', 'custom'];
var ENVIRONMENTS = ['dungeon', 'urban', 'wilderness', 'forest', 'desert', 'coast', 'mountain', 'swamp', 'underground', 'arctic', 'planar'];
var THEMES = ['noble', 'military', 'bandit', 'arcane', 'temple', 'undead', 'dragon', 'pirate', 'merchant', 'ancient', 'planar'];
var CONTAINERS = ['pouch', 'chest', 'crate', 'barrel', 'cabinet', 'desk', 'strongbox', 'cache', 'room', 'vault', 'shipwreck', 'shrine', 'pile'];
var ITEM_CATS = CATEGORIES; // includes 'coins'

function chip(active, label, onClick, key) {
  return <button key={key} type="button" className={'vi-tr-chip' + (active ? ' is-on' : '')} onClick={onClick}>{label}</button>;
}

function TreasureView() {
  const ctx = useContext(CombatContext);
  const cat = useTreasureCatalogue();
  const [mode, setMode] = useState('hoard');
  const [wealth, setWealth] = useState('standard');
  const [quantity, setQuantity] = useState('normal');
  const [level, setLevel] = useState(5);
  const [environment, setEnvironment] = useState('');
  const [theme, setTheme] = useState('');
  const [container, setContainer] = useState('');
  const [comp, setComp] = useState(null);       // null = auto; else { cat: % }
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [monsterModal, setMonsterModal] = useState(-1);

  var state = ctx.state || {};
  var monsters = state.monsters || [];
  var count = Math.min(state.activeMonsterCount || monsters.length, monsters.length);
  var catalogue = cat.catalogue;
  var ready = catalogue && catalogue.valid;
  var log = state.treasureLog || [];

  function req() {
    var r = { mode: mode, wealth: wealth, quantity: quantity, level: level, environment: environment, theme: theme, container: container };
    if (comp) { r.composition = comp; }
    return r;
  }
  function doGenerate() {
    if (!ready) { return; }
    setResult(withUids(generateTreasure(catalogue, Object.assign({}, req(), { seed: Date.now() }))));
    setEditing(null);
  }
  function onRemoveItem(it) { setResult(removeItem(result, it.uid)); }
  function onRerollItem(it) { setResult(rerollItem(catalogue, req(), result, it.uid)); }
  function onEditItem(it) { setEditing({ uid: it.uid, name: it.name, category: it.category, qty: it.qty, unitValue: it.unitValue }); }
  function onAddNew() { setEditing({ name: '', category: 'curios', qty: 1, unitValue: 0 }); }
  function submitEdit() {
    if (!editing || !editing.name) { setEditing(null); return; }
    var base = result || { coins: { counts: {}, value: 0 }, items: [], totalValue: 0 };
    setResult(upsertItem(base, editing)); setEditing(null);
  }
  function save() {
    if (!result) { return; }
    ctx.saveGeneralTreasure({ mode: mode, level: level, wealth: wealth, context: { environment: environment, theme: theme, container: container }, total: result.totalValue, result: result });
  }

  // composition editor helpers
  function setCompCat(c, v) {
    var next = Object.assign({}, comp || {});
    next[c] = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    setComp(next);
  }
  function compTotal() { var t = 0; var cc = comp || {}; Object.keys(cc).forEach(function (k) { t += Number(cc[k]) || 0; }); return t; }
  function normaliseComp() {
    var cc = comp || {}; var t = compTotal(); if (t <= 0) { return; }
    var next = {}; Object.keys(cc).forEach(function (k) { next[k] = Math.round((cc[k] / t) * 100); });
    setComp(next);
  }

  var populated = [];
  for (var i = 0; i < count; i++) {
    var m = monsters[i];
    if (m && ((m.customName && m.customName.trim()) || m.selectedMonster)) { populated.push({ m: m, i: i }); }
  }

  return (
    <div className="vi-view vi-treasure-view" id="view-treasure">
      <div className="vi-tr-page-head">
        <h2 className="vi-tr-page-title">&#128176; Treasure</h2>
        <p className="vi-tr-page-sub">Use the real VISINVOKE specialist generators through DCC, then assign approved structured results to canonical player or party possessions.</p>
      </div>

      <GeneratorProvenanceWorkspace />

      {!ready ? (
        <div className="vi-tr-panel vi-tr-empty">{cat.loading ? 'Loading treasure catalogue...' : ('Treasure catalogue unavailable' + (cat.error ? (': ' + cat.error) : '') + '. Coin and item content is provisioned separately; the system is ready once the catalogue is published.')}</div>
      ) : null}

      <div className="vi-tr-panel">
        <div className="vi-tr-panel-title">Monster Search</div>
        {populated.length === 0 ? (
          <div className="vi-tr-empty">No populated monsters in this encounter yet. Load or name monsters, then search each for treasure here or from the Treasure button beside Active Monster.</div>
        ) : (
          <div className="vi-tr-monster-grid">
            {populated.map(function (row) {
              var mctx = deriveMonsterContext(row.m);
              var t = row.m.treasure;
              var status = t ? t.status : 'unsearched';
              return (
                <div className="vi-tr-monster-card" key={row.i}>
                  <div className="vi-tr-monster-name">{deriveTabName(row.m, row.i)}</div>
                  <div className="vi-tr-monster-meta">Slot {row.i + 1} &middot; {mctx.creatureType} &middot; CR-lvl {mctx.level}</div>
                  <div className="vi-tr-monster-foot">
                    <span className={'vi-tr-status vi-tr-status-' + status}>{status.toUpperCase()}</span>
                    <button type="button" className="vi-tr-btn" onClick={function () { setMonsterModal(row.i); }}>Treasure</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="vi-tr-panel">
        <div className="vi-tr-panel-title">Legacy encounter treasure workspace</div>
        <p className="vi-tr-page-sub">This preserved encounter-local workspace remains available for existing saved combat records. New canonical possessions enter through the specialist-source boundary above.</p>

        <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Mode</span>{MODES.map(function (mo) { return chip(mode === mo, mo, function () { setMode(mo); }, mo); })}</div>
        <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Wealth</span>{WEALTH_TIERS.map(function (w) { return chip(wealth === w, w, function () { setWealth(w); }, w); })}</div>
        <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Quantity</span>{QUANT.map(function (q) { return chip(quantity === q, q, function () { setQuantity(q); }, q); })}</div>
        <div className="vi-tr-chiprow">
          <span className="vi-tr-chiplabel">Level</span>
          <button type="button" className="vi-tr-chip" onClick={function () { setLevel(Math.max(0, level - 1)); }}>&minus;</button>
          <span className="vi-tr-levelval">{level}</span>
          <button type="button" className="vi-tr-chip" onClick={function () { setLevel(Math.min(40, level + 1)); }}>+</button>
        </div>
        {mode === 'container' ? (
          <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Container</span>{CONTAINERS.map(function (c) { return chip(container === c, c, function () { setContainer(container === c ? '' : c); }, c); })}</div>
        ) : null}
        <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Environment</span>{ENVIRONMENTS.map(function (e) { return chip(environment === e, e, function () { setEnvironment(environment === e ? '' : e); }, e); })}</div>
        <div className="vi-tr-chiprow"><span className="vi-tr-chiplabel">Theme</span>{THEMES.map(function (th) { return chip(theme === th, th, function () { setTheme(theme === th ? '' : th); }, th); })}</div>

        <div className="vi-tr-comp">
          <div className="vi-tr-comp-head">
            <span className="vi-tr-chiplabel">Composition</span>
            <span className={'vi-tr-comp-total' + (comp && compTotal() !== 100 ? ' is-off' : '')}>{comp ? ('Total ' + compTotal() + '%') : 'Auto'}</span>
            {comp ? <button type="button" className="vi-tr-chip" onClick={normaliseComp}>Normalise to 100</button> : null}
            <button type="button" className="vi-tr-chip" onClick={function () { setComp(comp ? null : ITEM_CATS.reduce(function (o, c) { o[c] = 0; return o; }, {})); }}>{comp ? 'Back to Auto' : 'Manual %'}</button>
          </div>
          {comp ? (
            <div className="vi-tr-comp-grid">
              {ITEM_CATS.map(function (c) {
                return (
                  <div className="vi-tr-comp-cell" key={c}>
                    <label>{categoryLabel(c)}</label>
                    <input type="number" min="0" max="100" value={(comp[c] === undefined ? 0 : comp[c])} onChange={function (e) { setCompCat(c, e.target.value); }} />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="vi-tr-actionbar">
          <button type="button" className="vi-tr-primary" onClick={doGenerate}>{result ? '↻ Reroll All' : '⚒ Generate'}</button>
          {result ? <button type="button" className="vi-tr-btn" onClick={onAddNew}>+ Add Item</button> : null}
          {result ? <button type="button" className="vi-tr-btn" onClick={save}>&#128190; Save</button> : null}
          {result ? <button type="button" className="vi-tr-btn vi-tr-btn-danger" onClick={function () { setResult(null); setEditing(null); }}>Clear</button> : null}
        </div>

        {editing ? (
          <div className="vi-tr-editform">
            <input className="vi-tr-input" placeholder="Item name" value={editing.name} onChange={function (e) { setEditing(Object.assign({}, editing, { name: e.target.value })); }} />
            <select className="vi-tr-input" value={editing.category} onChange={function (e) { setEditing(Object.assign({}, editing, { category: e.target.value })); }}>
              {ITEM_CATS.filter(function (c) { return c !== 'coins'; }).map(function (c) { return <option key={c} value={c}>{categoryLabel(c)}</option>; })}
            </select>
            <input className="vi-tr-input vi-tr-input-sm" type="number" min="1" value={editing.qty} onChange={function (e) { setEditing(Object.assign({}, editing, { qty: e.target.value })); }} />
            <input className="vi-tr-input vi-tr-input-sm" type="number" min="0" placeholder="value" value={editing.unitValue} onChange={function (e) { setEditing(Object.assign({}, editing, { unitValue: e.target.value })); }} />
            <button type="button" className="vi-tr-primary" onClick={submitEdit}>Save</button>
            <button type="button" className="vi-tr-btn" onClick={function () { setEditing(null); }}>Cancel</button>
          </div>
        ) : null}

        {result ? <TreasureResult result={result} currency={catalogue.currency} actions={{ onRemoveItem: onRemoveItem, onRerollItem: onRerollItem, onEditItem: onEditItem }} /> : null}
      </div>

      <div className="vi-tr-panel">
        <div className="vi-tr-panel-title">Saved Treasure ({log.length})</div>
        {log.length === 0 ? <div className="vi-tr-empty">Saved hoards, containers and rewards appear here with the encounter. Generate and Save above.</div> : (
          <div className="vi-tr-history">
            {log.map(function (e) {
              return (
                <div className="vi-tr-history-row" key={e.id}>
                  <div className="vi-tr-history-main">
                    <span className="vi-tr-history-mode">{e.mode}</span>
                    <span className="vi-tr-history-meta">lvl {e.level} &middot; {e.wealth} &middot; {formatValue(e.total, catalogue ? catalogue.currency : null)}</span>
                  </div>
                  <div className="vi-tr-history-side">
                    <button type="button" className="vi-tr-mini" title="Load" onClick={function () { setResult(e.result); }}>&#8635;</button>
                    <button type="button" className="vi-tr-mini vi-tr-mini-danger" title="Delete" onClick={function () { ctx.deleteGeneralTreasure(e.id); }}>&times;</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MonsterTreasureModal index={monsterModal} isOpen={monsterModal >= 0} onClose={function () { setMonsterModal(-1); }} />
    </div>
  );
}

export default TreasureView;
